import React from 'react'
import { Button, InputNumber, Select } from 'antd'
import { Box, Slider, Typography } from '@mui/material'

import { ColorPickerInput } from './ColorPickerInput'

const DEFAULT_COLORS = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b']

function parseGradient(value: string): { angle: number; colors: string[] } | null {
  if (!value || typeof value !== 'string') return null
  const match = value.match(/linear-gradient\s*\(\s*(\d+)deg\s*,\s*(.+)\s*\)/)
  if (!match) return null
  const angle = parseInt(match[1], 10)
  const stopsStr = match[2]
  const colorRegex = /(#[a-f\d]{3,6}|rgb\s*\([^)]+\)|rgba\s*\([^)]+\))/gi
  const colors: string[] = []
  let m: RegExpExecArray | null
  while ((m = colorRegex.exec(stopsStr)) !== null) {
    colors.push(m[1].trim())
  }
  if (colors.length < 2) return null
  return { angle, colors }
}

function buildGradient(angle: number, colors: string[]): string {
  if (colors.length < 2) return ''
  const step = 100 / (colors.length - 1)
  const stops = colors.map((c, i) => `${c} ${Math.round(i * step)}%`).join(', ')
  return `linear-gradient(${angle}deg, ${stops})`
}

interface GradientInputProps {
  value?: string
  onChange?: (value: string) => void
  disabled?: boolean
}

export function GradientInput({ value = '', onChange, disabled }: GradientInputProps) {
  const parsed = React.useMemo(() => parseGradient(value), [value])
  const hasInteracted = React.useRef(false)

  const [colorCount, setColorCount] = React.useState(2)
  const [angle, setAngle] = React.useState(135)
  const [colors, setColors] = React.useState<string[]>(['#667eea', '#764ba2'])

  React.useEffect(() => {
    if (parsed) {
      setAngle(parsed.angle)
      setColors(parsed.colors)
      setColorCount(parsed.colors.length)
      hasInteracted.current = true
    } else if (!value) {
      hasInteracted.current = false
      setColorCount(2)
      setAngle(135)
      setColors([DEFAULT_COLORS[0], DEFAULT_COLORS[1]])
    }
  }, [value, parsed])

  const syncColorsToCount = React.useCallback((count: number) => {
    setColorCount(count)
    hasInteracted.current = true
    setColors((prev) => {
      const next = [...prev]
      while (next.length < count) {
        next.push(DEFAULT_COLORS[next.length % DEFAULT_COLORS.length])
      }
      return next.slice(0, count)
    })
  }, [])

  const handleColorCountChange = (v: number) => {
    hasInteracted.current = true
    const count = Math.min(5, Math.max(2, v))
    syncColorsToCount(count)
  }

  const handleAngleChange = (v: number) => {
    hasInteracted.current = true
    setAngle(v)
  }

  const handleColorChange = (index: number, color: string) => {
    hasInteracted.current = true
    setColors((prev) => {
      const next = [...prev]
      next[index] = color
      return next
    })
  }

  React.useEffect(() => {
    if (!hasInteracted.current && !value) return
    const gradient = buildGradient(angle, colors)
    if (gradient && onChange && gradient !== value) onChange(gradient)
  }, [angle, colors, onChange, value])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
          จำนวนสี
        </Typography>
        <Select
          value={colorCount}
          onChange={handleColorCountChange}
          disabled={disabled}
          options={[
            { value: 2, label: '2 สี' },
            { value: 3, label: '3 สี' },
            { value: 4, label: '4 สี' },
            { value: 5, label: '5 สี' },
          ]}
          style={{ width: 100 }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
          มุม (°)
        </Typography>
        <Box sx={{ flex: 1, minWidth: 120, maxWidth: 280 }}>
          <Slider
            value={angle}
            onChange={(_, v) => handleAngleChange(v as number)}
            min={0}
            max={360}
            disabled={disabled}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}°`}
            sx={{ mt: 0.5 }}
          />
        </Box>
        <InputNumber
          min={0}
          max={360}
          value={angle}
          onChange={(v) => handleAngleChange(v ?? 135)}
          disabled={disabled}
          addonAfter="°"
          style={{ width: 90 }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          สี
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {colors.map((c, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
              <ColorPickerInput
                value={c}
                onChange={(v) => handleColorChange(i, v)}
                disabled={disabled}
              />
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        <Box
          sx={{
            flex: 1,
            height: 48,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            background: buildGradient(angle, colors) || 'transparent',
          }}
        />
        {value && (
          <Button size="small" onClick={() => onChange?.('')} disabled={disabled}>
            ล้าง
          </Button>
        )}
      </Box>
    </Box>
  )
}
